import { Input as AInput, Checkbox, Col, Form, FormInstance, Row, Spin, Tooltip } from 'antd';
import React, { Dispatch, FC, useMemo, useState } from 'react';

import { debounce } from 'lodash';
import { FormattedMessage } from 'react-intl';
import styled from 'styles/styled-components';
import utilsMessages from 'utils/messages';

import Input from 'components/TrackingComponents/Input';

import translations from 'translations';

import { LoadingOutlined } from '@ant-design/icons';
import { compose } from 'redux';
import injectSaga from 'utils/injectSaga';
import messages from '../messages';

import { connect } from 'react-redux';

import { fetchExternalInvoice } from '../action';
import saga from './redInvoiceSaga';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import InfoIcon from './InfoIcon.svg';
import { Rule } from 'antd/es/form';

const FormItem = styled(Form.Item)`
  margin-bottom: 12px !important;
`;

const AntdInput = styled(AInput)<any>``;

interface Props {
  form?: FormInstance<any>;
  setFetchingInvoice?: (state: boolean) => void;
  required?: boolean;
  isValidateEmail?: boolean;
}

interface IDispatchProps {
  fetchRedInvoice: (taxCode: string, cb: (data: any) => void) => void;
}

interface IStateProps {}

type OwnProps = Props & IDispatchProps & IStateProps;

const antIcon = <LoadingOutlined style={{ fontSize: 18 }} spin />;

export const emailListRule: Rule = {
  validator: async (_, value?: string) => {
    const lang = window.localStorage.getItem('lang');
    if (!value) return Promise.resolve();

    if (value.length > 256) {
      return Promise.reject(
        new Error(
          lang === 'en'
            ? 'Invoice email address must not exceed 256 characters.'
            : 'Email nhận hoá đơn không được vượt quá 256 ký tự.',
        ),
      );
    }

    if (value !== value.trim()) {
      return Promise.reject(
        new Error(
          lang === 'en' ? 'Leading or trailing spaces are not allowed' : 'Không được có khoảng trắng ở đầu hoặc cuối',
        ),
      );
    }

    const parts = value.split(',');

    const hasSpacingSeparated = parts.find((part) => /\s/.test(part.trim()));
    if (hasSpacingSeparated) {
      return Promise.reject(
        new Error(
          lang === 'en'
            ? 'Email addresses must be separated by commas, not spaces'
            : 'Email phải được phân cách bằng dấu phẩy.',
        ),
      );
    }

    if (/,(\s*,)+/.test(value)) {
      return Promise.reject(
        new Error(lang === 'en' ? 'Consecutive commas are not allowed' : 'Không được có dấu phẩy liên tiếp'),
      );
    }

    const emails = value.split(/\s*,\s*/);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidEmail = emails.find((email) => !emailRegex.test(email));

    if (invalidEmail) {
      return Promise.reject(
        new Error(`${lang === 'en' ? 'Invalid email address' : 'Email không hợp lệ'}: ${invalidEmail}`),
      );
    }

    return Promise.resolve();
  },
};

export const invoiceAddressValidate: Rule = {
  validator: async (_, value?: string) => {
    const lang = window.localStorage.getItem('lang');
    if (!value) return Promise.resolve();

    if (value.length > 400) {
      return Promise.reject(
        new Error(
          lang === 'vi'
            ? 'Địa chỉ nhận hoá đơn không được vượt quá 400 ký tự.'
            : 'Invoice address must not exceed 400 characters.',
        ),
      );
    }
    return Promise.resolve();
  },
};

export const invoiceCompanyNameValidate: Rule = {
  validator: async (_, value?: string) => {
    const lang = window.localStorage.getItem('lang');
    if (!value) return Promise.resolve();

    if (value.length > 400) {
      return Promise.reject(
        new Error(
          lang === 'vi' ? 'Tên công ty không được vượt quá 400 ký tự.' : 'Company name must not exceed 400 characters.',
        ),
      );
    }
    return Promise.resolve();
  },
};

const RedInvoice: FC<OwnProps> = ({ form, fetchRedInvoice, setFetchingInvoice, required }) => {
  const [loading, setLoading] = useState(false);
  const watchIsSameAddress = Form.useWatch('isSameAddress', form);

  const debouncedRedInvoiceSearch = useMemo(
    () =>
      debounce(async (val: string) => {
        if (val === '' || form?.getFieldError('taxCode')?.length) {
          return;
        }
        setLoading(true);
        setFetchingInvoice?.(true);
        fetchRedInvoice(val, (data) => {
          if (data) {
            form?.setFieldsValue({
              redInvoiceCompanyName: data?.name || '',
              redInvoiceAddress: data?.address || '',
            });
          }
          setLoading(false);
          setFetchingInvoice?.(false);
        });
      }, 400),
    [fetchRedInvoice, form],
  );

  return (
    <>
      <Row gutter={12} id="first-row">
        <Col xs={24} lg={12}>
          <FormattedMessage {...messages.taxCode}>
            {(taxCode: string) => (
              <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: taxCode }}>
                {(fieldRequired) => (
                  <FormattedMessage {...messages.taxCodePlaceHolder}>
                    {(taxCodePlaceHolder) => (
                      <FormItem
                        name="taxCode"
                        colon={false}
                        required={false}
                        label={taxCode}
                        hasFeedback={form?.getFieldValue('taxCode')}
                        rules={[
                          {
                            required: !!required,
                            message: fieldRequired,
                          },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              const errorMessage = translations(messages.taxCodeInvalid);
                              if (!/^[0-9-]+$/.test(value)) {
                                return Promise.reject(new Error(errorMessage));
                              }
                              const isValid =
                                /^\d{10}$/.test(value) || /^\d{12}$/.test(value) || /^\d{10}-\d{3}$/.test(value);
                              if (!isValid) {
                                return Promise.reject(new Error(errorMessage));
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <AInput
                          placeholder={String(taxCodePlaceHolder)}
                          suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                          disabled={loading}
                          onChange={(event) => {
                            const value = event.target.value;
                            debouncedRedInvoiceSearch(value);
                            return value;
                          }}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </Col>
        <Col xs={24} lg={12}>
          <FormattedMessage {...messages.companyName}>
            {(companyName: string) => (
              <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: companyName }}>
                {(fieldRequired) => (
                  <FormattedMessage {...messages.companyNamePlaceHolder}>
                    {(companyNamePlaceHolder) => (
                      <FormItem
                        name="redInvoiceCompanyName"
                        label={companyName}
                        colon={false}
                        required={false}
                        hasFeedback={false}
                        rules={[{ required: !!required, message: fieldRequired }, invoiceCompanyNameValidate]}
                      >
                        <Input
                          InputComponent={AntdInput}
                          trackingCategory="Create Store Form | Input"
                          trackingAction="Enter Phone"
                          type="text"
                          placeHolder={companyNamePlaceHolder}
                          suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                          disabled={loading}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={24}>
          <FormattedMessage {...messages.emailReceiveRedInvoice}>
            {(emailReceiveRedInvoice: string) => (
              <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: emailReceiveRedInvoice }}>
                {(fieldRequired) => (
                  <FormattedMessage {...messages.emailReceiveRedInvoicPlaceHolder}>
                    {(emailReceiveRedInvoicPlaceHolder) => (
                      <FormItem
                        name="redInvoiceEmail"
                        colon={false}
                        required={false}
                        label={
                          <div style={{ display: 'flex', gap: 4 }}>
                            {emailReceiveRedInvoice}
                            <Tooltip
                              title={translations(messages.messageForEmail)}
                              overlayInnerStyle={{ width: '410px' }}
                            >
                              <SVGIcon src={InfoIcon} />
                            </Tooltip>
                          </div>
                        }
                        rules={[
                          {
                            required: false,
                            message: fieldRequired,
                          },
                          emailListRule,
                        ]}
                        hasFeedback={false}
                        validateTrigger={'onBlur'}
                      >
                        <Input
                          InputComponent={AntdInput}
                          trackingCategory="Create Store Form | Input"
                          trackingAction="Enter store name"
                          type="text"
                          placeHolder={emailReceiveRedInvoicPlaceHolder}
                          suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                          disabled={loading}
                        />
                      </FormItem>
                    )}
                  </FormattedMessage>
                )}
              </FormattedMessage>
            )}
          </FormattedMessage>
        </Col>
      </Row>
      <Form.Item
        name="isSameAddress"
        valuePropName="checked"
        colon={false}
        required={false}
        style={{ marginBottom: '2px' }}
      >
        <Checkbox>{translations(messages.redInvoiceAddressCheckbox)}</Checkbox>
      </Form.Item>
      {!watchIsSameAddress && (
        <Row gutter={12}>
          <Col span={24}>
            <FormattedMessage {...messages.redInvoiceAddress}>
              {(redInvoiceAddress: string) => (
                <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: redInvoiceAddress }}>
                  {(fieldRequired) => (
                    <FormattedMessage {...messages.redInvoiceAddressPlaceHolder}>
                      {(redInvoiceAddressPlaceHolder) => (
                        <FormItem
                          name="redInvoiceAddress"
                          colon={false}
                          required={false}
                          label={redInvoiceAddress}
                          rules={[
                            {
                              required: false,
                              message: fieldRequired,
                            },
                            invoiceAddressValidate,
                          ]}
                          hasFeedback={false}
                        >
                          <Input
                            InputComponent={AntdInput}
                            trackingCategory="Create Store Form | Input"
                            trackingAction="Enter store name"
                            type="text"
                            placeHolder={redInvoiceAddressPlaceHolder}
                            suffix={<Spin style={{ display: loading ? 'block' : 'none' }} indicator={antIcon} />}
                            disabled={loading}
                          />
                        </FormItem>
                      )}
                    </FormattedMessage>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          </Col>
        </Row>
      )}
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => ({
  fetchRedInvoice: (taxCode, cb) => dispatch(fetchExternalInvoice(taxCode, cb)),
});

const withConnect = connect<IStateProps, IDispatchProps>(() => ({}), mapDispatchToProps);

const withSaga = injectSaga<Props>({ key: 'redInvoice', saga: saga });

export default compose(withConnect, withSaga)(RedInvoice);
